// Os dois passos que a monetização acrescenta a TODO fluxo pós-ação, para cada
// host (treino, refeição, lição, mapa) encaixar com uma linha:
//
//   <PassoPreOfensiva>  LevelUp (se o SERVIDOR disse que subiu) → intersticial
//                       (se a regra deixar) → onFim. Vai ANTES da ofensiva.
//   <PassoVitaCoins>    a tela de VitaCoins, com o vídeo-bônus, no FIM da
//                       cadeia (depois do compartilhar). Sem crédito → onFim na
//                       hora, sem piscar nada.
//
// Encadeamento por useState/onFim, igual ao resto do app: nada de fila nova.

import React, { useEffect, useRef, useState } from 'react';
import Loader from '../../design/components/Loader/Loader';
import LevelUp from '../gamificacao/LevelUp';
import VitaCoinsBonus from '../recompensas/VitaCoinsBonus';
import { faixaDoNivel } from '../../design/components/LevelBadge/faixas';
import { aplicarGanho, saldoEmCache } from '../../services/moedaService';
import {
  bonusDisponivel,
  depoisDaAcao,
  estadoAtual,
  pedirRewarded,
  prepararBonus,
} from './monetizacao';
import { registroCelebrado } from '../avaliacao/pedirAvaliacao';
import s from './PassosPosAcao.module.scss';

export function PassoPreOfensiva({ levelUp, gatilho, onFim }) {
  const subiu = levelUp?.leveledUp === true && !levelUp?.repetido;
  const [etapa, setEtapa] = useState(subiu ? 'levelup' : 'anuncio');
  // Campanha própria / oferta do Premium abertas: a cobertura sai da frente.
  const [telaPropria, setTelaPropria] = useState(false);
  const fimRef = useRef(onFim);
  fimRef.current = onFim;

  useEffect(() => {
    if (etapa !== 'anuncio') return undefined;
    let vivo = true;
    const fim = () => {
      if (!vivo) return;
      vivo = false;
      fimRef.current?.();
    };
    if (!gatilho) {
      fim();
      return undefined;
    }
    depoisDaAcao(gatilho, {
      aoTelaPropria: (aberta) => { if (vivo) setTelaPropria(aberta); },
    }).finally(fim);
    return () => {
      vivo = false;
    };
  }, [etapa, gatilho]);

  if (etapa === 'levelup') {
    const antes = Number(levelUp.nivelAntes) || 1;
    const depois = Number(levelUp.nivelDepois) || antes + 1;
    return (
      <LevelUp
        nivel={depois}
        nivelAntes={antes}
        novaFaixa={faixaDoNivel(antes).chave !== faixaDoNivel(depois).chave}
        onClose={() => setEtapa('anuncio')}
      />
    );
  }
  // SEM GATILHO NÃO HÁ ESPERA: devolver a cobertura aqui faria um flash opaco
  // em todo fluxo que passa por este passo sem pedir anúncio.
  if (!gatilho) return null;
  // A cobertura fica a 12000 e o funil do Premium (1062) / campanha própria
  // (4000) abaixo: com ela na frente, a oferta pós-anúncio abria INVISÍVEL e o
  // slot esperava até 30 min por um toque — o "carregando eterno depois do
  // anúncio" (05/10/2026). Enquanto uma tela do app está aberta, nada aqui.
  if (telaPropria) return null;
  // COM GATILHO, SEGURA A TELA. Antes isto era `null`: a celebração desmontava,
  // o app aparecia por baixo e só então a próxima tela entrava — dava a
  // impressão de que o fluxo tinha fechado sozinho e o anúncio nunca abria.
  return (
    <div className={s.cobertura} role="status" aria-busy="true">
      <Loader />
    </div>
  );
}

export function PassoVitaCoins({ moeda, onFim }) {
  const valor = moeda?.creditou ? Number(moeda.valorFinal) || 0 : 0;
  const [est, setEst] = useState(null);
  const fimRef = useRef(onFim);
  fimRef.current = onFim;
  // Fim da sequência pós-registro: o momento do "Está gostando do app?" da
  // loja (pedirAvaliacao decide se é a hora).
  const terminar = () => {
    registroCelebrado();
    fimRef.current?.();
  };
  const terminarRef = useRef(terminar);
  terminarRef.current = terminar;

  useEffect(() => {
    if (valor <= 0) {
      terminarRef.current();
      return undefined;
    }
    let vivo = true;
    estadoAtual().then((e) => {
      if (!vivo) return;
      setEst(e);
      // A TELA ABRIU: abre a sessão e manda o vídeo carregar já, enquanto a
      // pessoa lê "Você ganhou N". Quando ela tocar no CTA o anúncio está
      // pronto, em vez de ela esperar sessão + carga em sequência.
      if (bonusDisponivel(moeda, e)) {
        prepararBonus({ tipo: 'BONUS', eventKeyOrigem: moeda.eventKey })
          .catch(() => {});
      }
    });
    return () => {
      vivo = false;
    };
  }, [valor, moeda]);

  if (valor <= 0 || !est) return null;

  const carteira = saldoEmCache();
  const saldo = carteira?.habilitado && Number.isFinite(Number(carteira.saldo))
    ? Number(carteira.saldo) : null;

  return (
    <VitaCoinsBonus
      valor={valor}
      saldo={saldo}
      multExibido={Number(est?.cfg?.BONUS_MULT_EXIBIDO) || 2.5}
      podeVideo={bonusDisponivel(moeda, est)}
      onAssistir={async () => {
        const creditar = (r) => aplicarGanho({
          creditou: true,
          valorFinal: r.valorBonus,
          valorBase: r.valorBonus,
          multiplicador: 1,
          saldoApos: r.saldoApos,
        });
        const r = await pedirRewarded({ tipo: 'BONUS', eventKeyOrigem: moeda.eventKey });
        if (r.status === 'CONCLUIDO') {
          creditar(r);
          return r;
        }
        // SSV atrasado: a carteira é atualizada quando ele chegar, mesmo que a
        // tela já tenha sido fechada. O crédito não depende de ninguém estar
        // olhando — só a animação depende.
        if (r.status === 'AGUARDANDO' && r.confirmacao) {
          return {
            ...r,
            confirmacao: r.confirmacao.then((tardio) => {
              if (tardio?.status === 'CONCLUIDO') creditar(tardio);
              return tardio;
            }),
          };
        }
        // Recusado pelo servidor (teto do dia, já resgatado): some o vídeo.
        if (r.status === 'RECUSADO') return { ...r, status: 'SEM_ANUNCIO' };
        return r;
      }}
      onClose={terminar}
    />
  );
}
